import { useCallback, useState, useRef } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";
import Autosuggest from "react-autosuggest";
import {
  mapStyles,
  stateCoordinates,
  mapConfig,
  mapStyle,
  mapboxToken,
} from "../../config";
import Button from "../Button";
import StatePopup from "../StatePopup";
import MapButtons from "../MapButtons";
import states from "../../data/states";
import { statesViews } from "../../constants";
import * as layers from "../../layers";
import { states as statesList } from "../../utils";
import { StatesViewToggle, MapSearchAutosuggest } from "./styled.js";
import {
  Legend,
  LegendTitle,
  LegendSwatch,
} from "../../components/Legend/styled.js";
import { Wrapper, Container } from "../../components/App/styled.js";
import {
  Input,
  Form,
  Icon,
  CancelButton,
  CancelIcon,
} from "../../components/Search/styled.js";

function StatesMap({ className, ...props }) {
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  const stateMapConfig = isMobile
    ? { ...mapConfig, zoom: 2, minZoom: 2 }
    : mapConfig;
  const [statesView, setStatesView] = useState(statesViews.CAPACITY);
  const [activeState, setActiveState] = useState("");
  const [activeHoverState, setActiveHoverState] = useState("");
  const [state, setState] = useState(null);
  const [mapViewState, setMapViewState] = useState(stateMapConfig);
  const mapRef = useRef();
  const [suggestions, setSuggestions] = useState([]);
  const [value, setValue] = useState("");

  const onClick = useCallback((event) => {
    const feature = event.features && event.features[0];

    if (
      feature &&
      (feature.layer.id === "state-fills-capacity" ||
        feature.layer.id === "state-fills-number-solar-schools" ||
        "states-hover")
    ) {
      const coordinates = stateCoordinates.find((x) => {
        return x[0] === feature.properties.name;
      });
      feature.properties.longitude = coordinates[7];
      feature.properties.latitude = coordinates[6];
      setState(feature.properties);
      setActiveState(feature.properties.name);
      mapRef.current.flyTo({
        center: [feature.properties.longitude, feature.properties.latitude],
        duration: 1000,
      });
      setValue("");
    }
  }, []);

  const onHover = useCallback((event) => {
    const feature = event.features && event.features[0];
    if (
      feature?.layer.id === "state-fills-capacity" ||
      feature?.layer.id === "state-fills-number-solar-schools"
    ) {
      setActiveHoverState(feature.properties.name);
      mapRef.current.getCanvas().style.cursor = "pointer";
    } else {
      mapRef.current.getCanvas().style.cursor = "";
    }
    if (!feature) {
      setActiveHoverState("");
    }
  }, []);

  const onMouseLeave = useCallback((event) => {
    setActiveHoverState("");
  }, []);

  const clearState = () => {
    setState(null);
    setValue("");
  };

  // Teach Autosuggest how to calculate suggestions for any given input value.
  const getSuggestions = (value) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    return inputLength === 0
      ? []
      : statesList.filter(
          (x) => x[0].toLowerCase().slice(0, inputLength) === inputValue
        );
  };

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  const getSuggestionValue = (suggestion) => suggestion[0];

  // Use your imagination to render suggestions.
  const renderSuggestion = (suggestion) => <div>{suggestion[0]}</div>;

  const onChange = (event, { newValue }) => {
    setValue(newValue);
  };

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  const onSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value));
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (event, suggestion) => {
    const feature = states.features.find((feature) => {
      return feature.properties.name === suggestion.suggestionValue;
    });
    if (feature) {
      setState(feature.properties);
      setActiveState(feature.properties.name);
      mapRef.current.flyTo({
        center: [feature.properties.longitude, feature.properties.latitude],
        duration: 1000,
      });
    } else {
      setState(null);
      setActiveState("");
    }
  };

  const renderInputComponent = (inputProps) => (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <Input {...inputProps} />
      {value.length > 0 && (
        <CancelButton aria-label="clear search" onClick={() => clearState()}>
          <CancelIcon viewBox="0 0 987.66 987.66">
            <path
              className="b"
              d="M970.38,17.28c-23.04-23.04-60.4-23.04-83.44,0L493.83,410.39,100.72,17.28c-23.04-23.04-60.4-23.04-83.44,0-23.04,23.04-23.04,60.4,0,83.44L410.39,493.83,17.28,886.94c-23.04,23.04-23.04,60.4,0,83.44,11.52,11.52,26.62,17.28,41.72,17.28s30.2-5.76,41.72-17.28l393.11-393.11,393.11,393.11c11.52,11.52,26.62,17.28,41.72,17.28s30.2-5.76,41.72-17.28c23.04-23.04,23.04-60.4,0-83.44l-393.11-393.11L970.38,100.72c23.04-23.04,23.04-60.4,0-83.44Z"
            />
          </CancelIcon>
        </CancelButton>
      )}
      <Icon src="/search.svg" alt="" />
    </Form>
  );

  const inputProps = {
    placeholder: "State Name",
    value,
    onChange: onChange,
  };

  return (
    <Wrapper>
      <MapSearchAutosuggest>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          onSuggestionSelected={onSuggestionSelected}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
          renderInputComponent={renderInputComponent}
        />
      </MapSearchAutosuggest>
      {/* <MapSearch onSearch={handleSearch} placeholder="State Name" /> */}
      <Container>
        <StatesViewToggle>
          <Button
            className="capacity"
            type="dark"
            img="true"
            isActive={statesView === statesViews.CAPACITY}
            onClick={() => setStatesView(statesViews.CAPACITY)}
          >
            <span className="label">Solar Capacity</span>
            <svg
              viewBox="0 0 16 3.5"
              htmlStyle="enable-background:new 0 0 16 3.5;"
            >
              <style type="text/css">
                {`.st0{fill:#FFFFFF;stroke:#B7E2FA;stroke-width:0.1;stroke-linecap:round;stroke-miterlimit:10;}`}
              </style>
              <path
                class="st0"
                d="M14.2,0.1h-14c0.2,0.1,1,0.6,1,1.7c0,1.1-0.9,1.6-1,1.7h14c0.5,0,1.7-0.4,1.7-1.7C15.9,0.4,14.7,0.1,14.2,0.1z"
              />
            </svg>

            <svg
              className="selected"
              viewBox="0 0 16 3.5"
              htmlStyle="enable-background:new 0 0 16 3.5;"
            >
              <style type="text/css">
                {`.st1{fill:#E7F4FC;stroke:#B7E2FA;stroke-width:0.1;stroke-linecap:round;stroke-miterlimit:10;}`}
              </style>
              <path
                class="st1"
                d="M14.2,0.1h-14c0.2,0.1,1,0.6,1,1.7c0,1.1-0.9,1.6-1,1.7h14c0.5,0,1.7-0.4,1.7-1.7C15.9,0.4,14.7,0.1,14.2,0.1z"
              />
            </svg>
          </Button>
          <Button
            className="number-schools"
            type="dark"
            img="true"
            isActive={statesView === statesViews.PERCENT_SCHOOLS}
            onClick={() => setStatesView(statesViews.PERCENT_SCHOOLS)}
          >
            <span className="label">Number of Solar Schools</span>
            <svg
              viewBox="0 0 22.3 3.5"
              htmlStyle="enable-background:new 0 0 22.3 3.5;"
            >
              <style type="text/css">
                {`.st0{fill:#FFFFFF;stroke:#B7E2FA;stroke-width:0.1;stroke-linecap:round;stroke-miterlimit:10;}`}
              </style>
              <path
                class="st0"
                d="M20.5,0.1H0.2c0.2,0.1,1,0.6,1,1.7c0,1.1-0.9,1.6-1,1.7h20.3c0.5,0,1.7-0.4,1.7-1.7C22.2,0.4,21,0.1,20.5,0.1z"
              />
            </svg>
            <svg
              className="selected"
              viewBox="0 0 22.3 3.5"
              htmlStyle="enable-background:new 0 0 22.3 3.5;"
            >
              <style type="text/css">
                {`.st1{fill:#E7F4FC;stroke:#B7E2FA;stroke-width:0.1;stroke-linecap:round;stroke-miterlimit:10;}`}
              </style>
              <path
                class="st1"
                d="M20.5,0.1H0.2c0.2,0.1,1,0.6,1,1.7c0,1.1-0.9,1.6-1,1.7h20.3c0.5,0,1.7-0.4,1.7-1.7C22.2,0.4,21,0.1,20.5,0.1z"
              />
            </svg>
          </Button>
        </StatesViewToggle>
        {statesView === statesViews.CAPACITY && (
          <Legend>
          <LegendTitle>Solar Capacity</LegendTitle>
          <ul>
            <li>
              <LegendSwatch
                color={mapStyles.legendDistrictTier1Color}
              ></LegendSwatch>{" "}
              {`0-999 kW`}
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier2Color}
              ></LegendSwatch>
              1,000-4,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier3Color}
              ></LegendSwatch>
              5,000-9,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier4Color}
              ></LegendSwatch>
              10,000-19,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier5Color}
              ></LegendSwatch>
              20,000-29,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier6Color}
              ></LegendSwatch>
              30,000-39,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier7Color}
              ></LegendSwatch>
              40,000+ kW
            </li>
          </ul>
        </Legend>
        )}
        {statesView === statesViews.PERCENT_SCHOOLS && (
          <Legend>
            <LegendTitle>
              Number of
              <br />
              Solar Schools
            </LegendTitle>
            <ul>
              <li>
                <LegendSwatch color={mapStyles.legendTier1Color}></LegendSwatch>{" "}
                0-24
              </li>
              <li>
                <LegendSwatch color={mapStyles.legendTier2Color}></LegendSwatch>
                25-49
              </li>
              <li>
                <LegendSwatch color={mapStyles.legendTier3Color}></LegendSwatch>
                50-99
              </li>
              <li>
                <LegendSwatch color={mapStyles.legendTier4Color}></LegendSwatch>
                100-149
              </li>
              <li>
                <LegendSwatch color={mapStyles.legendTier5Color}></LegendSwatch>
                150-249
              </li>
              <li>
                <LegendSwatch color={mapStyles.legendTier6Color}></LegendSwatch>
                250-499
              </li>
              <li>
                <LegendSwatch color={mapStyles.legendTier7Color}></LegendSwatch>
                500+
              </li>
            </ul>
          </Legend>
        )}
        <Map
          {...mapViewState}
          id="statesMap"
          ref={mapRef}
          onMove={(evt) => setMapViewState(evt.viewState)}
          width="100%"
          height="100%"
          mapStyle={mapStyle}
          mapboxAccessToken={mapboxToken}
          onClick={onClick}
          onMouseMove={onHover}
          onMouseLeave={onMouseLeave}
          interactiveLayerIds={[
            "state-fills-capacity",
            "state-fills-number-solar-schools",
            "state-hover",
          ]}
        >
          <NavigationControl showCompass={false} />
          <Source id="states" type="geojson" data={states}>
            <Layer
              id="state-fills-capacity"
              {...layers.stateFillsCapacity}
              layout={{
                visibility:
                  statesView === statesViews.CAPACITY ? "visible" : "none",
              }}
            />
            <Layer
              id="state-fills-number-solar-schools"
              {...layers.stateFillsNumberSolarSchools}
              layout={{
                visibility:
                  statesView === statesViews.PERCENT_SCHOOLS
                    ? "visible"
                    : "none",
              }}
            />
            {/* <Layer
              id="states-hover"
              type="fill"
              paint={{ "fill-color": "#fff", "fill-opacity": 1 }}
              filter={["==", "name", activeState]}
            /> */}
            <Layer
              id="state-hover"
              {...layers.stateHoverLayer}
              filter={["==", "name", activeHoverState]}
            />
            <Layer
              id="active-state"
              {...layers.activeStateLayer}
              filter={["==", "name", activeState]}
            />
            <Layer id="state-borders" {...layers.stateBordersStatesLayer} />
          </Source>
          {state && (
            <StatePopup
              anchor="bottom"
              longitude={Number(state.longitude)}
              latitude={Number(state.latitude)}
              onClose={() => clearState()}
              closeOnClick={false}
              data={state}
              view={statesView}
            ></StatePopup>
          )}
        </Map>{" "}
        <MapButtons mapRef={mapRef} showStates={true} />
      </Container>
    </Wrapper>
  );
}

export default StatesMap;
